import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';

/**
 * Environment-aware logging service that protects sensitive data.
 *
 * In production:
 * - Debug, info, and warning messages are suppressed
 * - Error messages are logged without sensitive details
 *
 * In development:
 * - All log levels are enabled
 * - Sensitive data (tokens, passwords) is still redacted
 */
@Injectable({
    providedIn: 'root'
})
export class LoggerService {
    private readonly isProduction: boolean;
    private readonly PREFIX = '[CARO]';

    /** Fields that should never be logged */
    private readonly SENSITIVE_FIELDS = [
        'token',
        'accessToken',
        'refreshToken',
        'password',
        'newPassword',
        'confirmPassword',
        'secret',
        'apiKey',
        'authorization'
    ];

    constructor() {
        this.isProduction = environment.production;
    }

    /**
     * Log debug message (development only)
     */
    debug(message: string, data?: any): void {
        if (!this.isProduction) {
            const sanitizedData = this.sanitize(data);
            if (sanitizedData !== undefined) {
                console.log(`${this.PREFIX} ${message}`, sanitizedData);
            } else {
                console.log(`${this.PREFIX} ${message}`);
            }
        }
    }

    /**
     * Log info message (development only)
     */
    info(message: string, data?: any): void {
        if (!this.isProduction) {
            const sanitizedData = this.sanitize(data);
            if (sanitizedData !== undefined) {
                console.log(`${this.PREFIX} ${message}`, sanitizedData);
            } else {
                console.log(`${this.PREFIX} ${message}`);
            }
        }
    }

    /**
     * Log warning message (development only)
     */
    warn(message: string, data?: any): void {
        if (!this.isProduction) {
            const sanitizedData = this.sanitize(data);
            if (sanitizedData !== undefined) {
                console.warn(`${this.PREFIX} ${message}`, sanitizedData);
            } else {
                console.warn(`${this.PREFIX} ${message}`);
            }
        }
    }

    /**
     * Log error message (always logged, but without sensitive data in production)
     */
    error(message: string, data?: any): void {
        if (this.isProduction) {
            // In production, log only the message without details
            console.error(`${this.PREFIX} ${message}`);
        } else {
            // In development, include sanitized details
            const sanitizedData = this.sanitize(data);
            if (sanitizedData !== undefined) {
                console.error(`${this.PREFIX} ${message}`, sanitizedData);
            } else {
                console.error(`${this.PREFIX} ${message}`);
            }
        }
    }

    /**
     * Sanitize data by redacting sensitive fields
     */
    private sanitize(data: any): any {
        if (data === undefined || data === null) {
            return undefined;
        }

        if (typeof data !== 'object') {
            return data;
        }

        if (Array.isArray(data)) {
            return data.map(item => this.sanitize(item));
        }

        const sanitized: any = {};
        for (const key of Object.keys(data)) {
            if (this.isSensitiveField(key)) {
                sanitized[key] = '[REDACTED]';
            } else if (typeof data[key] === 'object' && data[key] !== null) {
                sanitized[key] = this.sanitize(data[key]);
            } else {
                sanitized[key] = data[key];
            }
        }
        return sanitized;
    }

    /**
     * Check if a field name is sensitive (case-insensitive)
     */
    private isSensitiveField(fieldName: string): boolean {
        const lowerField = fieldName.toLowerCase();
        return this.SENSITIVE_FIELDS.some(sensitive =>
            lowerField.includes(sensitive.toLowerCase())
        );
    }
}
